import { List, useTable, DateField } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import {
  Table,
  Tag,
  Row,
  Col,
  Card,
  Alert,
  Button,
  Space,
  Typography,
  Tooltip as AntTooltip,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import { useMemo } from "react";
import type { HealthCheck } from "@/lib/types";
import { StatCard } from "@/components/StatCard";
import { intFmt } from "@/lib/format";
import { melColors } from "@/theme/tokens";

const { Text, Title } = Typography;

/** Frequência do auto-refresh (ms). A API real expõe GET /admin/health. */
const REFRESH_MS = 15_000;

const STATUS: Record<
  HealthCheck["status"],
  { color: string; hex: string; icon: React.ReactNode; label: string; weight: number }
> = {
  up: {
    color: "green",
    hex: melColors.success,
    icon: <CheckCircleOutlined />,
    label: "UP",
    weight: 0,
  },
  degraded: {
    color: "orange",
    hex: melColors.warning,
    icon: <WarningOutlined />,
    label: "DEGRADADO",
    weight: 1,
  },
  down: {
    color: "red",
    hex: melColors.error,
    icon: <CloseCircleOutlined />,
    label: "DOWN",
    weight: 2,
  },
};

/** Faixas de latência → cor do texto (verde rápido, âmbar lento, vermelho ruim). */
function latencyColor(ms: number | null): string {
  if (ms == null) return melColors.inkSoft;
  if (ms < 500) return melColors.success;
  if (ms < 1500) return melColors.warning;
  return melColors.error;
}

/** Extrai "N waiting" / "N failed" do detalhe da fila BullMQ. */
function parseQueue(detail: string | null): { waiting: number; failed: number } | null {
  if (!detail) return null;
  const waiting = detail.match(/(\d+)\s*waiting/i);
  const failed = detail.match(/(\d+)\s*failed/i);
  if (!waiting && !failed) return null;
  return { waiting: Number(waiting?.[1] ?? 0), failed: Number(failed?.[1] ?? 0) };
}

/** Extrai "X/Y conexões" do detalhe do Postgres (pool). */
function parsePool(detail: string | null): { used: number; max: number } | null {
  if (!detail) return null;
  const m = detail.match(/(\d+)\s*\/\s*(\d+)/);
  if (!m) return null;
  return { used: Number(m[1]), max: Number(m[2]) };
}

/**
 * /admin/health — SAÚDE DO SISTEMA.
 *
 * Como obtém os dados (MOCK MODE): `useList` com `pagination: { mode: "off" }`
 * traz TODOS os componentes (postgres, redis, bullmq, providers de IA, Stripe,
 * SMTP) dos fixtures. Agregamos no cliente para o banner de status geral, os
 * cards (componentes UP, fila BullMQ, pool Postgres, latência média) e a grade
 * de indicadores. Uma `useTable` separada alimenta a tabela detalhada paginável.
 *
 * Auto-refresh: `refetchInterval` re-busca a cada 15s (polling), simulando o
 * monitor ao vivo. Quando a API real estiver pronta, basta desligar VITE_MOCK —
 * o mesmo resource "health" passa a ler de GET /admin/health (simple-rest).
 */
export const HealthList: React.FC = () => {
  // Todas as linhas para os cards/grade + polling.
  const {
    data: allData,
    isLoading,
    isFetching,
    refetch,
  } = useList<HealthCheck>({
    resource: "health",
    pagination: { mode: "off" },
    queryOptions: { refetchInterval: REFRESH_MS },
  });
  const rows = useMemo(() => allData?.data ?? [], [allData]);

  // Tabela detalhada (paginada, ordenável) — ligada ao dataProvider.
  const { tableProps } = useTable<HealthCheck>({
    resource: "health",
    syncWithLocation: true,
    sorters: { initial: [{ field: "component", order: "asc" }] },
  });

  // ── Agregações ──
  const agg = useMemo(() => {
    let up = 0;
    let degraded = 0;
    let down = 0;
    let latSum = 0;
    let latCount = 0;
    let lastChecked: string | null = null;
    let queue: { waiting: number; failed: number } | null = null;
    let pool: { used: number; max: number } | null = null;

    for (const r of rows) {
      if (r.status === "up") up++;
      else if (r.status === "degraded") degraded++;
      else down++;
      if (r.latency_ms != null) {
        latSum += r.latency_ms;
        latCount++;
      }
      if (!lastChecked || r.checked_at > lastChecked) lastChecked = r.checked_at;
      if (r.component === "bullmq") queue = parseQueue(r.detail) ?? queue;
      if (r.component === "postgres") pool = parsePool(r.detail) ?? pool;
    }
    return {
      up,
      degraded,
      down,
      total: rows.length,
      avgLatency: latCount ? Math.round(latSum / latCount) : 0,
      lastChecked,
      queue,
      pool,
    };
  }, [rows]);

  // Status geral: o pior status entre os componentes manda no banner.
  const overall: HealthCheck["status"] =
    agg.down > 0 ? "down" : agg.degraded > 0 ? "degraded" : "up";
  const overallCfg = STATUS[overall];

  return (
    <List
      title="Saúde do sistema"
      headerButtons={
        <Space>
          {agg.lastChecked && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Checado{" "}
              <DateField value={agg.lastChecked} format="DD/MM/YYYY HH:mm:ss" />
            </Text>
          )}
          <Button
            icon={<ReloadOutlined spin={isFetching} />}
            onClick={() => refetch()}
            loading={isFetching}
          >
            Atualizar
          </Button>
        </Space>
      }
    >
      {/* Banner de status geral */}
      <Alert
        type={overall === "up" ? "success" : overall === "degraded" ? "warning" : "error"}
        showIcon
        icon={overallCfg.icon}
        style={{ marginBottom: 16 }}
        message={
          <Space>
            <Text strong>
              {overall === "up"
                ? "Todos os sistemas operacionais"
                : overall === "degraded"
                ? "Operando com degradação"
                : "Incidente em andamento"}
            </Text>
            <Text type="secondary">
              · {agg.up} UP / {agg.degraded} degradado(s) / {agg.down} fora do ar
            </Text>
          </Space>
        }
        description={
          <Space size={[8, 4]} wrap style={{ marginTop: 4 }}>
            <span>Atualização automática a cada {REFRESH_MS / 1000}s.</span>
            {isFetching && <Spin size="small" />}
          </Space>
        }
      />

      {/* Cards de resumo */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Componentes UP"
            value={agg.total ? `${agg.up}/${agg.total}` : "—"}
            prefix={<HeartOutlined />}
            hint={
              agg.down + agg.degraded > 0
                ? `${agg.degraded} degradado(s) · ${agg.down} down`
                : "Tudo saudável"
            }
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Fila (BullMQ)"
            value={agg.queue ? intFmt.format(agg.queue.waiting) : "—"}
            prefix={<ThunderboltOutlined />}
            suffix={agg.queue ? "waiting" : undefined}
            hint={
              agg.queue
                ? agg.queue.failed > 0
                  ? `${agg.queue.failed} job(s) com falha`
                  : "0 falhas"
                : "fila classify"
            }
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Pool Postgres"
            value={agg.pool ? `${agg.pool.used}/${agg.pool.max}` : "—"}
            prefix={<DatabaseOutlined />}
            suffix={agg.pool ? "conexões" : undefined}
            hint={
              agg.pool
                ? `${Math.round((agg.pool.used / agg.pool.max) * 100)}% do pool em uso`
                : "Neon"
            }
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Latência média"
            value={agg.total ? agg.avgLatency : "—"}
            prefix={<ClockCircleOutlined />}
            suffix={agg.total ? "ms" : undefined}
            hint="média dos componentes com latência"
            loading={isLoading}
          />
        </Col>
      </Row>

      {/* Grade de indicadores por componente */}
      <Card
        className="mel-glass"
        bordered={false}
        title="Componentes"
        style={{ marginBottom: 16 }}
        loading={isLoading}
      >
        <Row gutter={[12, 12]}>
          {rows.map((r) => {
            const cfg = STATUS[r.status] ?? STATUS.down;
            return (
              <Col xs={24} sm={12} lg={8} xl={6} key={r.id}>
                <Card
                  size="small"
                  bordered={false}
                  className="mel-glass"
                  style={{ borderLeft: `4px solid ${cfg.hex}` }}
                >
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                    align="start"
                  >
                    <Space direction="vertical" size={2}>
                      <Text strong style={{ textTransform: "capitalize" }}>
                        {r.component}
                      </Text>
                      <Tag color={cfg.color} icon={cfg.icon} style={{ marginInlineEnd: 0 }}>
                        {cfg.label}
                      </Tag>
                    </Space>
                    <Space direction="vertical" size={0} align="end">
                      <AntTooltip title="Latência da última verificação">
                        <Text
                          strong
                          style={{ color: latencyColor(r.latency_ms), fontSize: 18 }}
                        >
                          {r.latency_ms == null ? "—" : `${intFmt.format(r.latency_ms)}ms`}
                        </Text>
                      </AntTooltip>
                    </Space>
                  </Space>
                  {r.detail && (
                    <Text
                      type="secondary"
                      style={{ display: "block", marginTop: 8, fontSize: 12 }}
                    >
                      {r.detail}
                    </Text>
                  )}
                </Card>
              </Col>
            );
          })}
          {!isLoading && rows.length === 0 && (
            <Col span={24}>
              <Title level={5} type="secondary" style={{ textAlign: "center", padding: 24 }}>
                Nenhum componente monitorado.
              </Title>
            </Col>
          )}
        </Row>
      </Card>

      {/* Tabela detalhada — ligada ao dataProvider (paginada/ordenável) */}
      <Card className="mel-glass" bordered={false} title="Histórico de verificações">
        <Table {...tableProps} rowKey="id" scroll={{ x: 800 }}>
          <Table.Column
            dataIndex="component"
            title="Componente"
            sorter
            render={(v: string) => (
              <Text strong style={{ textTransform: "capitalize" }}>
                {v}
              </Text>
            )}
          />
          <Table.Column
            dataIndex="status"
            title="Status"
            sorter
            render={(s: HealthCheck["status"]) => {
              const cfg = STATUS[s] ?? STATUS.down;
              return (
                <Tag color={cfg.color} icon={cfg.icon}>
                  {cfg.label}
                </Tag>
              );
            }}
          />
          <Table.Column
            dataIndex="latency_ms"
            title="Latência"
            sorter
            render={(v: number | null) =>
              v == null ? (
                <Text type="secondary">—</Text>
              ) : (
                <Text style={{ color: latencyColor(v) }}>{intFmt.format(v)} ms</Text>
              )
            }
          />
          <Table.Column
            dataIndex="detail"
            title="Detalhe"
            render={(v: string | null) => v ?? <Text type="secondary">—</Text>}
          />
          <Table.Column
            dataIndex="checked_at"
            title="Checado"
            sorter
            render={(v: string) => <DateField value={v} format="DD/MM/YYYY HH:mm:ss" />}
          />
        </Table>
      </Card>
    </List>
  );
};
